import { numEdgeKey } from "../modeling/topology.ts";
import type { PolygonTopology } from "../modeling/types.ts";

export type PrimitiveSymmetryAxis = "x" | "y" | "z";

const AXIS_INDEX: Record<PrimitiveSymmetryAxis, number> = { x: 0, y: 1, z: 2 };

function quantize(v: number, epsilon: number): number {
  return Math.round(v / epsilon);
}

function positionKey(
  positions: Float32Array,
  vertexIndex: number,
  epsilon: number,
  axisIndex?: number,
): string {
  const base = vertexIndex * 3;
  const qx = quantize(positions[base], epsilon);
  const qy = quantize(positions[base + 1], epsilon);
  const qz = quantize(positions[base + 2], epsilon);
  if (axisIndex === 0) return `${-qx}|${qy}|${qz}`;
  if (axisIndex === 1) return `${qx}|${-qy}|${qz}`;
  if (axisIndex === 2) return `${qx}|${qy}|${-qz}`;
  return `${qx}|${qy}|${qz}`;
}

function computeMatchEpsilon(positions: Float32Array): number {
  if (positions.length < 3) return 1e-5;
  let minX = positions[0],
    minY = positions[1],
    minZ = positions[2];
  let maxX = positions[0],
    maxY = positions[1],
    maxZ = positions[2];
  for (let i = 3; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  const extent = Math.max(maxX - minX, maxY - minY, maxZ - minZ);
  return Math.max(extent * 1e-4, 1e-5);
}

/**
 * Find topology edges whose mirrored counterpart is missing for the given axis.
 *
 * Matching is performed in object-local space against the polygon topology
 * that object-mode wireframe already uses, so the debug overlay stays aligned
 * with modifier-evaluated primitive geometry.
 */
export function findAsymmetricTopologyEdges(
  positions: Float32Array,
  topology: PolygonTopology,
  axis: PrimitiveSymmetryAxis,
): Set<number> {
  const edgeCount = topology.edges.length / 2;
  if (positions.length === 0 || edgeCount === 0) return new Set();

  const axisIndex = AXIS_INDEX[axis];
  const epsilon = computeMatchEpsilon(positions);
  const vertexCount = positions.length / 3;
  const verticesByKey = new Map<string, number[]>();
  for (let vi = 0; vi < vertexCount; vi++) {
    const key = positionKey(positions, vi, epsilon);
    const matches = verticesByKey.get(key);
    if (matches) matches.push(vi);
    else verticesByKey.set(key, [vi]);
  }

  const edgeKeys = new Set<number>();
  for (let ei = 0; ei < edgeCount; ei++) {
    edgeKeys.add(
      numEdgeKey(topology.edges[ei * 2], topology.edges[ei * 2 + 1]),
    );
  }

  const asymmetric = new Set<number>();
  for (let ei = 0; ei < edgeCount; ei++) {
    const a = topology.edges[ei * 2];
    const b = topology.edges[ei * 2 + 1];
    const mirroredA = verticesByKey.get(
      positionKey(positions, a, epsilon, axisIndex),
    );
    const mirroredB = verticesByKey.get(
      positionKey(positions, b, epsilon, axisIndex),
    );
    if (!mirroredA || !mirroredB) {
      asymmetric.add(ei);
      continue;
    }

    let matched = false;
    for (let i = 0; i < mirroredA.length && !matched; i++) {
      for (let j = 0; j < mirroredB.length; j++) {
        if (edgeKeys.has(numEdgeKey(mirroredA[i], mirroredB[j]))) {
          matched = true;
          break;
        }
      }
    }
    if (!matched) asymmetric.add(ei);
  }

  return asymmetric;
}
